import { Capacitor } from '@capacitor/core';

const IOS_NOTCH_INSET = 59;
const CORNER_INSET = 12;
const NOTCH_THRESHOLD = 20;

function measureInset(side) {
  const probe = document.createElement('div');
  const prop = `safe-area-inset-${side}`;
  probe.style.cssText = [
    'position: fixed',
    'top: 0',
    'left: 0',
    `padding-${side}: env(${prop})`,
    `padding-${side}: constant(${prop})`,
    'visibility: hidden',
    'pointer-events: none',
  ].join(';');
  document.body.appendChild(probe);
  const value = parseFloat(window.getComputedStyle(probe)[`padding${side.charAt(0).toUpperCase()}${side.slice(1)}`]) || 0;
  document.body.removeChild(probe);
  return value;
}

/** Which long edge has the notch in landscape (WebView inset values are often wrong). */
function getLandscapeNotchSide() {
  const type = window.screen?.orientation?.type;
  if (type === 'landscape-secondary') return 'right';
  if (type === 'landscape-primary') return 'left';

  const angle = window.screen?.orientation?.angle ?? window.orientation ?? 0;
  // landscape-secondary ≈ notch on the right (home button on the left).
  if (angle === 270 || angle === -90) return 'right';
  return 'left';
}

function notchInset(measured, isIos) {
  if (measured >= NOTCH_THRESHOLD) return measured;
  return isIos ? IOS_NOTCH_INSET : CORNER_INSET;
}

/** Notch is on one long edge in landscape — pad that edge only. */
function resolveLandscapeNotchInsets(left, right, isIos) {
  if (!isIos) {
    return {
      landscapeNotchLeft: Math.max(CORNER_INSET, left),
      landscapeNotchRight: Math.max(CORNER_INSET, right),
    };
  }

  const notchSide = getLandscapeNotchSide();

  if (notchSide === 'left') {
    return {
      landscapeNotchLeft: notchInset(left, isIos),
      landscapeNotchRight: CORNER_INSET,
    };
  }

  return {
    landscapeNotchLeft: CORNER_INSET,
    landscapeNotchRight: notchInset(right, isIos),
  };
}

/** Measure safe-area insets and expose as CSS custom properties (iOS WebView fallback). */
export default function applySafeAreaInsets() {
  const insets = {
    top: measureInset('top'),
    right: measureInset('right'),
    bottom: measureInset('bottom'),
    left: measureInset('left'),
  };

  const isLandscape = window.matchMedia('(orientation: landscape)').matches;
  const isIos = Capacitor.getPlatform() === 'ios';

  if (isIos && !isLandscape && insets.top === 0) {
    insets.top = 59;
  }

  const minSide = 16;
  if (!isLandscape) {
    insets.left = Math.max(minSide, insets.left);
    insets.right = Math.max(minSide, insets.right);
  }

  const { landscapeNotchLeft, landscapeNotchRight } = isLandscape
    ? resolveLandscapeNotchInsets(insets.left, insets.right, isIos)
    : { landscapeNotchLeft: 0, landscapeNotchRight: 0 };

  const root = document.documentElement;
  root.style.setProperty('--safe-area-top', `${insets.top}px`);
  root.style.setProperty('--safe-area-right', `${insets.right}px`);
  root.style.setProperty('--safe-area-bottom', `${insets.bottom}px`);
  root.style.setProperty('--safe-area-left', `${insets.left}px`);
  root.style.setProperty('--landscape-notch-left', `${landscapeNotchLeft}px`);
  root.style.setProperty('--landscape-notch-right', `${landscapeNotchRight}px`);
}
